import { CustomFonts } from 'app/components/custom-fonts';
import React, { Fragment } from 'react';
import { Spinner } from 'app/components/ui/spinner';
import { ErrorModal } from 'app/components/error-modal';
import { useTheme } from 'app/shared/contexts/theme';
import { useBuilding } from 'app/shared/contexts/building';
import { useErrors } from 'app/shared/contexts/errors';
import { ErrorPage } from 'app/pages/error';
import { useApi } from 'app/shared/contexts/api';
import { useAnalytics } from 'app/shared/hooks/use-analytics-hook';

interface AppContentWrapperProps {
  children: React.ReactNode;
}

export const AppContentWrapper = ({ children }: AppContentWrapperProps): JSX.Element => {
  const { theme } = useTheme();
  const { building } = useBuilding();
  const { genericErrors } = useErrors();
  const { apiUrl } = useApi();

  // Analytics + web-tracking init. This used to hang off AppRoot, which renders nothing but a
  // <Navigate> off `/` — it unmounted before the building and user queries resolved, so the
  // effects only ever ran with null data: Sentry kept a null user for the whole session and fell
  // back to identifying people by IP, and web tracking never started. This wrapper is mounted for
  // every route, above every early return below.
  // Sentry only receives the user this sets if your miniapp adds its own `src/app/sentry.ts`
  // calling `init` with its project DSN and imports it from `src/index.tsx`; the template has none.
  useAnalytics();

  if (genericErrors.length > 0 || !apiUrl) {
    return <ErrorPage />;
  }

  if (theme && building) {
    return (
      <Fragment>
        <CustomFonts />
        <div className="max-h-screen bg-background text-foreground pt-4">
          {children}
          <ErrorModal />
        </div>
      </Fragment>
    );
  }

  return (
    <div
      className="flex flex-col justify-center h-screen"
      data-testid="loading-container"
    >
      <Spinner />
    </div>
  );
};
